import { useState } from "react";
import { Link } from "react-router-dom";
import AuthImage from "./AuthImage";
import Lightbox from "./Lightbox";
import { MapPin, ShieldCheck, User as UserIcon, Pencil, Mail, Share2 } from "lucide-react";
import FollowButton from "./FollowButton";

/**
 * Shared profile header.
 *  - Banner (full-width, fixed height)
 *  - Avatar bottom-anchored to banner, overlaps banner by 50% of its height
 *  - Username + headline + location text positioned to RIGHT of avatar
 *    with a minimum 20px left margin from the avatar's right edge
 *  - Mobile (< md): avatar stacks above centered text — no overlap
 *  - Action buttons (Edit / Follow / Share) sit far right on desktop, wrap on mobile
 *
 * Used by Profile.jsx for People, Organization, and Admin profiles.
 */
export default function ProfileHeader({ profile, onShare, onFollowChange, testId = "profile-header" }) {
  const [lightbox, setLightbox] = useState(null);
  const displayName = profile.full_name || profile.organization_name;
  const isOrg = profile.type === "organization";
  const isSelf = profile.is_self;
  const handle = profile.handle;
  const headline = profile.headline;
  const location = [profile.city, profile.country].filter(Boolean).join(", ");

  // Avatar size: 112px desktop / 96px mobile. Half-overlap = 56px / 48px.
  return (
    <div className="csc-card overflow-hidden" data-testid={testId}>
      {/* Banner */}
      <div className="relative h-44 lg:h-56 bg-gradient-to-br from-[#0d1b2e] via-[#0d1b2e] to-[#050d1a]" data-testid="profile-banner">
        <AuthImage src={profile.banner_url} alt="" className="w-full h-full object-cover cursor-zoom-in" onClick={() => profile.banner_url && setLightbox([{ url: profile.banner_url }])} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(5,13,26,0) 30%, rgba(5,13,26,0.85) 100%)" }} />
      </div>

      {/* Header row */}
      <div className="px-6 lg:px-8 pb-6 relative">
        {/* Avatar — anchored to bottom-left of banner, overlapping by 50% of its height */}
        <div
          className="absolute left-6 lg:left-8 -top-12 lg:-top-14 w-24 h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden border-4 border-[#0d1b2e] bg-[#050d1a] cursor-zoom-in"
          style={{ boxShadow: "0 0 22px rgba(0, 170, 255, 0.35)" }}
          onClick={() => profile.avatar_url && setLightbox([{ url: profile.avatar_url }])}
          data-testid="profile-avatar"
        >
          <AuthImage
            src={profile.avatar_url}
            alt={displayName}
            className="w-full h-full object-cover"
            fallback={
              <div className="w-full h-full flex items-center justify-center bg-[#050d1a]">
                {isOrg ? <ShieldCheck size={28} className="text-[#00aaff]" /> : <UserIcon size={28} className="text-[#39ff14]" />}
              </div>
            }
          />
        </div>

        {/* Desktop layout (md+): text row, indented past avatar.
            Mobile (< md): centered stack below the avatar. */}
        <div className="flex flex-col items-center text-center md:items-end md:text-left md:flex-row md:gap-4 pt-16 md:pt-4 md:pl-[148px] lg:pl-[168px]">
          <div className="flex-1 min-w-0 w-full">
            <div className="flex items-center md:items-center gap-2 flex-wrap justify-center md:justify-start">
              <h1 className="text-2xl lg:text-3xl font-bold text-white truncate" style={{ letterSpacing: "-0.02em" }} data-testid="profile-name">
                {displayName}
              </h1>
              <span
                className={`font-mono-tech text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded ${isOrg ? "bg-[#00aaff]/15 text-[#00aaff] border border-[#00aaff]/40" : "bg-[#39ff14]/15 text-[#39ff14] border border-[#39ff14]/40"}`}
                data-testid="profile-type-badge"
              >
                {isOrg ? "Organization" : "People"}
              </span>
            </div>
            {handle && <div className="font-mono-tech text-[12px] text-[#94a3b8] mt-1" data-testid="profile-handle">@{handle}</div>}
            {headline && <p className="text-sm text-[#94a3b8] mt-1" data-testid="profile-headline">{headline}</p>}
            {location && (
              <p className="font-mono-tech text-[11px] text-[#94a3b8] mt-2 inline-flex items-center gap-1.5" data-testid="profile-location">
                <MapPin size={12} className="text-[#00aaff]" /> {location}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap justify-center mt-4 md:mt-0 md:flex-nowrap">
            {isSelf ? (
              <Link
                to={isOrg ? `/org/${profile.id}/edit` : "/profile/edit"}
                className="csc-btn-primary"
                data-testid="edit-profile-btn"
              >
                <Pencil size={13} /> Edit Profile
              </Link>
            ) : (
              <>
                <FollowButton profile={profile} onChange={onFollowChange} />
                <button className="csc-btn-ghost" data-testid="message-btn" title="Direct messages — go to Chat" disabled>
                  <Mail size={14} /> Message
                </button>
              </>
            )}
            <button className="csc-btn-ghost" onClick={onShare} data-testid="share-profile-btn">
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>
      </div>
      {lightbox && <Lightbox images={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
